import { useViewportWidth } from '../../lib/useViewportWidth.ts';
import { CHAT_DRAWER_MIN_WIDTH } from '../chat/chatDrawerWidth.ts';
import { RAIL_WIDTH } from './rail.tsx';

/**
 * How wide a page's content column is normally drawn — `AppShell`'s `max-w-240`.
 *
 * Written here as a number because where the drawer stands is arithmetic, and a
 * cap that lives only in a utility class cannot be added up. It is also the
 * default floor: a page that has not said otherwise wants its full column.
 */
export const CONTENT_MAX_WIDTH = 960;

export interface ChatDrawerFitOptions {
  /** The width the user asked for. That is the one that is remembered. */
  wanted: number;
  /**
   * How narrow this page's content may be squeezed before the drawer has to
   * lie over it. Defaults to the full column; a page whose content reads fine
   * in less says so itself.
   */
  contentMin?: number;
  /** Zero where the shell is drawn without a rail. */
  railWidth?: number;
  /** Left out to ask the window. */
  viewportWidth?: number;
}

export interface ChatDrawerFit {
  /** How wide the drawer is actually drawn. */
  width: number;
  /** Whether it stands beside the page rather than over it. */
  pushes: boolean;
}

/**
 * Where the chat drawer stands, and how wide, at this window.
 *
 * The drawer gives way before it gives up: as the window narrows it shrinks,
 * down to the narrowest it can still be used at, and only when even that no
 * longer fits beside the page does it lie over it — at the width that was
 * asked for, because covering the page is not the moment to also make it
 * narrow. So the width the user set is remembered whatever the window does,
 * and the width on screen is that one, capped by the room there is for it.
 *
 * One function rather than one sum per caller: the shell reserving the track
 * and the drawer filling it have to agree to the pixel, and two copies of this
 * would eventually disagree.
 */
export function chatDrawerFit({
  wanted,
  contentMin = CONTENT_MAX_WIDTH,
  railWidth = RAIL_WIDTH,
  viewportWidth = document.documentElement.clientWidth,
}: ChatDrawerFitOptions): ChatDrawerFit {
  const room = viewportWidth - railWidth - contentMin;
  const pushes = room >= CHAT_DRAWER_MIN_WIDTH;

  return {
    width: pushes ? Math.min(wanted, Math.max(CHAT_DRAWER_MIN_WIDTH, room)) : wanted,
    pushes,
  };
}

/** The same answer, kept up to date as the window is resized. */
export function useChatDrawerFit(options: ChatDrawerFitOptions): ChatDrawerFit {
  const viewportWidth = useViewportWidth();

  return chatDrawerFit({ ...options, viewportWidth });
}
